/**
 * ProductsEmbeddedView
 * Embedded version of ProductModal for inline display in Facturation tab.
 * Reuses the same hooks and logic but displays inline (no modal overlay).
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  PlusIcon,
  MinusIcon,
  TrashIcon,
  PencilIcon,
  MagnifyingGlassIcon,
  CubeIcon,
  BuildingLibraryIcon,
  ChevronDownIcon,
  WrenchScrewdriverIcon,
  XMarkIcon,
  ArrowTopRightOnSquareIcon
} from '@heroicons/react/24/outline';
import {
  useAffaireProducts,
  useBillingSummary,
  useAddAffaireProduct,
  useUpdateAffaireProduct,
  useRemoveAffaireProduct
} from '../../hooks/useProducts';
import {
  useTarifBanques,
  useTarifServices,
  useTarifPrestations,
  useAffaireContext
} from '../../hooks/useTarifs';
import { useAffaireMonthlyTracks } from '../../hooks/useInvoices';
import type {
  AffaireProduct,
  AffaireMonthlyTrack,
  AddAffaireProductData,
  PayerTrack,
  TarifPrestation
} from '../../types/facturation.types';
import '../../facturation.css';

// ============================================================================
// UTILITIES
// ============================================================================

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('fr-BE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  }).format(amount);
};

const calculateReductionAmount = (
  unitPrice: number,
  reductionType: 'percentage' | 'fixed' | null | undefined,
  reductionValue: number | null | undefined
): number => {
  if (!reductionType || !reductionValue) return 0;
  if (reductionType === 'percentage') {
    return unitPrice * (reductionValue / 100);
  }
  return reductionValue;
};

// ============================================================================
// SUB-COMPONENTS (copied from ProductModal for standalone use)
// ============================================================================

interface DropdownProps {
  icon: React.ReactNode;
  placeholder: string;
  items: Array<{ code: string; name: string }>;
  selectedCode: string | null;
  onSelect: (code: string) => void;
  isLoading: boolean;
  disabled?: boolean;
}

const Dropdown: React.FC<DropdownProps> = ({
  icon,
  placeholder,
  items,
  selectedCode,
  onSelect,
  isLoading,
  disabled
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selectedItem = items.find(item => item.code === selectedCode);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="pm-dropdown" ref={ref}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className="pm-dropdown-trigger"
      >
        <span className="pm-dropdown-trigger-content">
          {icon}
          <span className={`pm-dropdown-trigger-text ${!selectedItem ? 'pm-dropdown-trigger-text--placeholder' : ''}`}>
            {selectedItem ? selectedItem.name : placeholder}
          </span>
        </span>
        <ChevronDownIcon className={`pm-dropdown-chevron ${isOpen ? 'pm-dropdown-chevron--open' : ''}`} />
      </button>

      {isOpen && !disabled && (
        <div className="pm-dropdown-menu">
          {isLoading ? (
            <div className="pm-dropdown-loading">Chargement...</div>
          ) : items.length === 0 ? (
            <div className="pm-dropdown-empty">Aucun element</div>
          ) : (
            items.map((item) => (
              <button
                key={item.code}
                type="button"
                onClick={() => {
                  onSelect(item.code);
                  setIsOpen(false);
                }}
                className={`pm-dropdown-item ${selectedCode === item.code ? 'pm-dropdown-item--selected' : ''}`}
              >
                <span className="pm-dropdown-item-name">{item.name}</span>
                <span className="pm-dropdown-item-code">{item.code}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

interface QtyStepperProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  disabled?: boolean;
}

const QtyStepper: React.FC<QtyStepperProps> = ({ value, onChange, min = 1, disabled }) => (
  <div className="pm-qty-stepper">
    <button
      type="button"
      className="pm-qty-stepper-btn"
      onClick={() => onChange(Math.max(min, value - 1))}
      disabled={disabled || value <= min}
    >
      <MinusIcon />
    </button>
    <span className="pm-qty-stepper-value">{value}</span>
    <button
      type="button"
      className="pm-qty-stepper-btn"
      onClick={() => onChange(value + 1)}
      disabled={disabled}
    >
      <PlusIcon />
    </button>
  </div>
);

interface CartRowProps {
  product: AffaireProduct;
  isEditing: boolean;
  isAnimating: boolean;  // For qty change pulse
  isNew: boolean;  // For new line appear
  onEdit: () => void;
  onCancelEdit: () => void;
  onSaveEdit: (data: {
    quantity?: number;
    unitPriceHtva?: number;
    unitPriceTvac?: number;
    honoraireExpertHtva?: number | null;
  }) => void;
  onDelete: () => void;
  onQtyChange: (qty: number) => void;
  isUpdating: boolean;
  isDeleting: boolean;  // For delete loading state
}

const CartRow: React.FC<CartRowProps> = ({
  product,
  isEditing,
  isAnimating,
  isNew,
  onEdit,
  onCancelEdit,
  onSaveEdit,
  onDelete,
  onQtyChange,
  isUpdating,
  isDeleting
}) => {
  const [editQty, setEditQty] = useState(product.quantity);
  const [editUnitPriceHtva, setEditUnitPriceHtva] = useState<number>(product.unit_price_htva);
  const [editUnitPriceTvac, setEditUnitPriceTvac] = useState<number>(
    product.unit_price_tvac || product.unit_price_htva * (1 + product.tva_rate / 100)
  );
  const [editHonoraire, setEditHonoraire] = useState<number | ''>(
    product.honoraire_expert_htva || ''
  );

  // Calculate initial TVAC from HTVA
  const tvaMultiplier = 1 + product.tva_rate / 100;

  useEffect(() => {
    if (isEditing) {
      setEditQty(product.quantity);
      setEditUnitPriceHtva(product.unit_price_htva);
      setEditUnitPriceTvac(
        product.unit_price_tvac || product.unit_price_htva * tvaMultiplier
      );
      setEditHonoraire(product.honoraire_expert_htva || '');
    }
  }, [isEditing, product, tvaMultiplier]);

  // When HTVA changes, recalculate TVAC (round to 2 decimals for display)
  const handleHtvaChange = (htva: number) => {
    setEditUnitPriceHtva(htva);
    setEditUnitPriceTvac(Math.round(htva * tvaMultiplier * 100) / 100);
  };

  // When TVAC changes, recalculate HTVA
  // IMPORTANT: Don't round HTVA - store full precision so TVAC can be recalculated exactly
  // e.g., TVAC=100 → HTVA=82.6446280991... → 82.6446... × 1.21 = 100.00
  const handleTvacChange = (tvac: number) => {
    setEditUnitPriceTvac(tvac);
    setEditUnitPriceHtva(tvac / tvaMultiplier);  // Full precision, no rounding
  };

  const handleSave = () => {
    onSaveEdit({
      quantity: editQty,
      unitPriceHtva: editUnitPriceHtva,
      unitPriceTvac: editUnitPriceTvac,
      honoraireExpertHtva: editHonoraire || null
    });
  };

  const reductionAmount = calculateReductionAmount(
    product.unit_price_htva,
    product.reduction_type,
    product.reduction_value
  );
  const netUnitPrice = product.unit_price_htva - reductionAmount;
  const lineHtva = product.quantity * netUnitPrice;

  // Use stored TVAC when available and no reduction to avoid rounding errors
  // When there's a reduction, we must recalculate since reduction is applied to HTVA
  const hasReduction = reductionAmount > 0;
  let lineTvac: number;
  let lineTva: number;
  if (!hasReduction && product.unit_price_tvac) {
    // Use exact TVAC from tarif (no rounding errors)
    lineTvac = product.quantity * product.unit_price_tvac;
    lineTva = lineTvac - lineHtva;  // Derive TVA as difference (exact)
  } else {
    // Calculate from HTVA (may have small rounding differences)
    lineTva = lineHtva * (product.tva_rate / 100);
    lineTvac = lineHtva + lineTva;
  }
  // Prefer the freshly-computed percentage returned by the enriched
  // getAffaireProducts query; fall back to the persisted column for safety
  // (in case the backend hasn't deployed the enrichment yet).
  const billedPct = Math.round(
    product.computed_billed_percentage ?? product.billed_percentage ?? 0
  );
  const queuedInDraft = !!product.queued_in_monthly_draft;
  const lastInvoice = product.invoices && product.invoices.length > 0
    ? product.invoices[product.invoices.length - 1]
    : null;

  const editorTotal = editQty * editUnitPriceHtva;
  const impactDiff = editorTotal - lineHtva;

  // Determine animation class (only one at a time)
  const animationClass = isNew ? 'pm-cart-row-main--new' : isAnimating ? 'pm-cart-row-main--animating' : '';

  return (
    <div className="pm-cart-row">
      <div className={`pm-cart-row-main ${animationClass}`}>
        {/* Column 1: Product Info */}
        <div className="pm-cart-row-info">
          <div className="pm-cart-row-name">
            <div className="pm-cart-row-name-header">
              <span className="pm-cart-row-code">{product.product_code}</span>
              {product.reduction_type && product.reduction_value && (
                <span className="pm-cart-row-badge pm-cart-row-badge--reduction">
                  -{product.reduction_type === 'percentage'
                    ? `${product.reduction_value}%`
                    : formatCurrency(product.reduction_value)}
                </span>
              )}
              {billedPct >= 100 ? (
                <span className="pm-cart-row-badge pm-cart-row-badge--billed">
                  {lastInvoice
                    ? `Facturée • ${lastInvoice.invoice_number}${lastInvoice.invoice_type === 'MONTHLY' ? ' (mensuelle)' : ''}`
                    : 'Facturée'}
                </span>
              ) : billedPct > 0 ? (
                <span className="pm-cart-row-badge pm-cart-row-badge--partial">
                  {billedPct}% facturée
                </span>
              ) : queuedInDraft ? (
                <span className="pm-cart-row-badge pm-cart-row-badge--queued">
                  En attente facture mensuelle (brouillon)
                </span>
              ) : product.payer_track === 'BANK_GROUPED' ? (
                <span className="pm-cart-row-badge pm-cart-row-badge--monthly">
                  → Prochaine facture mensuelle{product.tarif_banque_name ? ` · ${product.tarif_banque_name}` : ''}
                </span>
              ) : (
                <span className="pm-cart-row-badge pm-cart-row-badge--customer">
                  → À facturer au client
                </span>
              )}
            </div>
            <span className="pm-cart-row-desc">{product.description}</span>
          </div>
          {product.banque_code && (
            <div className="pm-cart-row-meta">
              {product.banque_code} / {product.service_code}
            </div>
          )}
        </div>

        {/* Column 2: Quantity + Prices */}
        <div className="pm-cart-row-middle">
          <QtyStepper
            value={product.quantity}
            onChange={onQtyChange}
            disabled={isUpdating || isEditing}
          />
          <div className="pm-cart-row-value">{formatCurrency(lineHtva)}</div>
          <div className="pm-cart-row-value pm-cart-row-value--tva">{formatCurrency(lineTva)}</div>
        </div>

        {/* Column 3: Total + Actions */}
        <div className="pm-cart-row-right">
          <div className="pm-cart-row-value pm-cart-row-value--total">{formatCurrency(lineTvac)}</div>
          <div className="pm-cart-row-actions">
            <button
              type="button"
              className="pm-cart-row-action"
              onClick={onEdit}
              title="Modifier"
              disabled={isEditing}
            >
              <PencilIcon />
            </button>
            <button
              type="button"
              className={`pm-cart-row-action pm-cart-row-action--danger ${isDeleting ? 'pm-cart-row-action--loading' : ''}`}
              onClick={onDelete}
              title="Supprimer"
              disabled={isDeleting || isEditing}
            >
              {isDeleting ? <div className="pm-spinner-small" /> : <TrashIcon />}
            </button>
          </div>
        </div>
      </div>

      {isEditing && (
        <div className="pm-cart-row-editor">
          <div className="pm-editor-grid">
            <div className="pm-editor-field">
              <label className="pm-editor-label">Quantite</label>
              <QtyStepper value={editQty} onChange={setEditQty} />
            </div>

            <div className="pm-editor-field">
              <label className="pm-editor-label">Hon. Expert (TVAC)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={editHonoraire}
                onChange={(e) => setEditHonoraire(e.target.value ? parseFloat(e.target.value) : '')}
                className="pm-editor-input"
                placeholder="Montant interne"
              />
              <span className="pm-editor-hint">(non affiche sur facture)</span>
            </div>

            <div className="pm-editor-field">
              <label className="pm-editor-label">Montant HTVA</label>
              <input
                type="number"
                min="0"
                step="0.0001"
                value={Math.round(editUnitPriceHtva * 10000) / 10000}
                onChange={(e) => handleHtvaChange(parseFloat(e.target.value) || 0)}
                className="pm-editor-input"
                placeholder="Prix unitaire HTVA"
              />
            </div>

            <div className="pm-editor-field">
              <label className="pm-editor-label">Montant TVAC</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={editUnitPriceTvac}
                onChange={(e) => handleTvacChange(parseFloat(e.target.value) || 0)}
                className="pm-editor-input"
                placeholder="Prix unitaire TVAC"
              />
              <span className="pm-editor-hint">TVA {product.tva_rate}%</span>
            </div>

            {impactDiff !== 0 && (
              <div className="pm-editor-impact">
                Impact: {impactDiff > 0 ? '+' : ''}{formatCurrency(impactDiff)} HTVA
              </div>
            )}

            <div className="pm-editor-actions">
              <button type="button" className="pm-btn-editor-cancel" onClick={onCancelEdit}>
                Annuler
              </button>
              <button
                type="button"
                className="pm-btn-editor-save"
                onClick={handleSave}
                disabled={isUpdating}
              >
                Sauvegarder
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export interface ProductsEmbeddedViewProps {
  affaireId: string;
  onProductsChanged?: () => void;
}

// Group products by payer track for the cart display
const groupProductsByPayerTrack = (products: AffaireProduct[] | undefined) => {
  const groups: Record<PayerTrack, AffaireProduct[]> = {
    CUSTOMER: [],
    BANK_GROUPED: []
  };
  if (!products) return groups;
  for (const product of products) {
    const track: PayerTrack = product.payer_track ?? 'CUSTOMER';
    groups[track].push(product);
  }
  return groups;
};

export const ProductsEmbeddedView: React.FC<ProductsEmbeddedViewProps> = ({
  affaireId,
  onProductsChanged
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Selection state
  const [selectedBanqueCode, setSelectedBanqueCode] = useState<string | null>(null);
  const [selectedServiceCode, setSelectedServiceCode] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // UI state
  const [editingProductId, setEditingProductId] = useState<number | null>(null);
  const [animatingProductId, setAnimatingProductId] = useState<number | null>(null);  // For qty change pulse
  const [newProductCode, setNewProductCode] = useState<string | null>(null);  // For new line appear
  const [clickedPrestationId, setClickedPrestationId] = useState<number | null>(null);
  const [deletingProductId, setDeletingProductId] = useState<number | null>(null);  // For delete loading state
  const [deleteError, setDeleteError] = useState<string | null>(null);  // For delete error message

  // Data fetching - Tarifs
  const { data: banques, isLoading: isLoadingBanques } = useTarifBanques();
  const { data: services, isLoading: isLoadingServices } = useTarifServices();
  const { data: prestations, isLoading: isLoadingPrestations } = useTarifPrestations(
    selectedBanqueCode || '',
    selectedServiceCode || ''
  );

  // Affaire context (remembered selections)
  const { data: affaireContext } = useAffaireContext(affaireId);

  // Affaire products (enriched with payer_track + computed_billed_percentage + invoices)
  const { data: affaireProducts, isLoading: isLoadingProducts, refetch: refetchProducts } = useAffaireProducts(affaireId);
  const { data: summary, refetch: refetchSummary } = useBillingSummary(affaireId);

  // Monthly billing tracks attached to this affaire (FACTURE MENSUELLE contacts)
  const { data: monthlyTracks } = useAffaireMonthlyTracks(affaireId);

  // Sectioned view: group products by payer track so customer-side vs
  // bank-grouped lines are visually distinct on mixed-payer affaires.
  const productGroups = useMemo(() => groupProductsByPayerTrack(affaireProducts), [affaireProducts]);
  const hasMixedPayers = productGroups.CUSTOMER.length > 0 && productGroups.BANK_GROUPED.length > 0;

  // Pick a single representative monthly track for the bank-grouped section
  // header. (V1 affaires almost always have one FACTURE MENSUELLE contact;
  // when there are several we still display the first — the section is a
  // hint, not the source of truth.)
  const primaryMonthlyTrack: AffaireMonthlyTrack | null = (monthlyTracks && monthlyTracks.length > 0)
    ? monthlyTracks[0]
    : null;

  // True when at least one bank-grouped product has not been invoiced yet
  // (and no DRAFT line exists for it either). This drives the State-A CTA.
  const bankSectionHasUnbilled = productGroups.BANK_GROUPED.some(
    p => (p.computed_billed_percentage ?? 0) <= 0 && !p.queued_in_monthly_draft
  );
  // True when at least one bank-grouped product is sitting on a DRAFT MONTHLY
  // invoice. Used to switch the CTA copy.
  const bankSectionHasDraft = productGroups.BANK_GROUPED.some(p => !!p.queued_in_monthly_draft);

  const handleOpenGroupedFacturation = useCallback(() => {
    if (!primaryMonthlyTrack) return;
    const basePath = location.pathname.includes('/bureau/') ? '/bureau' : '/admin';
    navigate(`${basePath}/facturation`, {
      state: {
        fromTab: 'recherche',
        preselectedContactId: primaryMonthlyTrack.billing_contact_id,
        highlightAffaireId: affaireId,
        from: location.pathname
      }
    });
  }, [primaryMonthlyTrack, location.pathname, navigate, affaireId]);

  // Mutations
  const addProductMutation = useAddAffaireProduct();
  const updateProductMutation = useUpdateAffaireProduct();
  const removeProductMutation = useRemoveAffaireProduct();

  // Filter prestations by search
  const filteredPrestations = useMemo(() => {
    if (!prestations) return [];
    if (!searchTerm.trim()) return prestations;
    const term = searchTerm.toLowerCase();
    return prestations.filter(p =>
      p.prestation_name.toLowerCase().includes(term) ||
      p.prestation_code.toLowerCase().includes(term)
    );
  }, [prestations, searchTerm]);

  // Pre-select remembered banque/service from affaire context
  useEffect(() => {
    if (affaireContext) {
      if (affaireContext.banque_code && !selectedBanqueCode) {
        setSelectedBanqueCode(affaireContext.banque_code);
      }
      if (affaireContext.service_code && !selectedServiceCode) {
        setSelectedServiceCode(affaireContext.service_code);
      }
    }
  }, [affaireContext]);

  // Reset service when banque changes
  const handleBanqueChange = (code: string) => {
    setSelectedBanqueCode(code);
    setSelectedServiceCode(null);
    setSearchTerm('');
  };

  const handleServiceChange = (code: string) => {
    setSelectedServiceCode(code);
    setSearchTerm('');
  };

  // Add prestation to cart
  const handleAddPrestation = useCallback(async (prestation: TarifPrestation) => {
    try {
      // Trigger click animation on prestation item
      setClickedPrestationId(prestation.id);
      setTimeout(() => setClickedPrestationId(null), 300);

      const existing = affaireProducts?.find(p => p.product_code === prestation.prestation_code);
      if (existing) {
        // Existing product: trigger pulse animation for qty change
        setAnimatingProductId(existing.id);
        setTimeout(() => setAnimatingProductId(null), 600);

        await updateProductMutation.mutateAsync({
          affaireId,
          productId: existing.id,
          data: { quantity: existing.quantity + 1 }
        });
      } else {
        // New product: trigger appear animation
        setNewProductCode(prestation.prestation_code);
        setTimeout(() => setNewProductCode(null), 600);

        const data: AddAffaireProductData = {
          productCode: prestation.prestation_code,
          description: prestation.prestation_name,
          quantity: 1,
          unitPriceHtva: prestation.price_htva,
          unitPriceTvac: prestation.price_tvac,  // Pass TVAC to avoid rounding errors
          tvaRate: prestation.tva_rate,
          honoraireExpertHtva: prestation.honoraire_expert_htva || undefined,
          tarifId: prestation.id,
          banqueCode: selectedBanqueCode || undefined,
          serviceCode: selectedServiceCode || undefined
        };
        await addProductMutation.mutateAsync({ affaireId, data });
      }
      refetchProducts();
      refetchSummary();
      onProductsChanged?.();
    } catch (error: any) {
      console.error('Failed to add prestation:', error);
    }
  }, [affaireId, affaireProducts, selectedBanqueCode, selectedServiceCode, addProductMutation, updateProductMutation, refetchProducts, refetchSummary, onProductsChanged]);

  // Quick qty change - triggers animation
  const handleQtyChange = useCallback(async (productId: number, newQty: number) => {
    try {
      // Trigger animation
      setAnimatingProductId(productId);
      setTimeout(() => setAnimatingProductId(null), 600);

      await updateProductMutation.mutateAsync({
        affaireId,
        productId,
        data: { quantity: newQty }
      });
      refetchProducts();
      refetchSummary();
      onProductsChanged?.();
    } catch (error: any) {
      console.error('Failed to update qty:', error);
    }
  }, [affaireId, updateProductMutation, refetchProducts, refetchSummary, onProductsChanged]);

  // Save edited product
  const handleSaveEdit = useCallback(async (productId: number, data: {
    quantity?: number;
    unitPriceHtva?: number;
    unitPriceTvac?: number;
    honoraireExpertHtva?: number | null;
  }) => {
    try {
      await updateProductMutation.mutateAsync({ affaireId, productId, data });
      refetchProducts();
      refetchSummary();
      onProductsChanged?.();
      setEditingProductId(null);
    } catch (error: any) {
      console.error('Failed to update product:', error);
    }
  }, [affaireId, updateProductMutation, refetchProducts, refetchSummary, onProductsChanged]);

  // Delete product (immediate, no confirmation)
  const handleDeleteProduct = useCallback(async (productId: number) => {
    setDeleteError(null);
    setDeletingProductId(productId);
    try {
      await removeProductMutation.mutateAsync({ affaireId, productId });
      refetchProducts();
      refetchSummary();
      onProductsChanged?.();
    } catch (error: any) {
      console.error('Failed to remove product:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Impossible de supprimer ce produit';
      setDeleteError(errorMessage);
      // Auto-dismiss error after 5 seconds
      setTimeout(() => setDeleteError(null), 5000);
    } finally {
      setDeletingProductId(null);
    }
  }, [affaireId, removeProductMutation, refetchProducts, refetchSummary, onProductsChanged]);

  const productCount = affaireProducts?.length || 0;
  const totalTvac = summary?.totalTvac || 0;

  return (
    <div className="fact-products-embedded">
      {/* Body - Two columns */}
      <div className="fact-products-embedded-body">
        {/* Left: Catalogue */}
        <div className="pm-catalogue">
          <div className="pm-catalogue-card">
            <h3 className="pm-catalogue-title">Ajouter une prestation</h3>

            {/* Step 1: Banque selection */}
            {!selectedBanqueCode && (
              <div className="pm-field">
                <label className="pm-field-label">1. Banque</label>
                <Dropdown
                  icon={<BuildingLibraryIcon />}
                  placeholder="Selectionner une banque..."
                  items={(banques || []).map(b => ({ code: b.banque_code, name: b.banque_name }))}
                  selectedCode={selectedBanqueCode}
                  onSelect={handleBanqueChange}
                  isLoading={isLoadingBanques}
                />
              </div>
            )}

            {/* Step 2: Service selection */}
            {selectedBanqueCode && !selectedServiceCode && (
              <div className="pm-field">
                <label className="pm-field-label">2. Service</label>
                <Dropdown
                  icon={<WrenchScrewdriverIcon />}
                  placeholder="Selectionner un service..."
                  items={(services || []).map(s => ({ code: s.service_code, name: s.service_name }))}
                  selectedCode={selectedServiceCode}
                  onSelect={handleServiceChange}
                  isLoading={isLoadingServices}
                />
              </div>
            )}

            {/* Step 3: Prestation search - shown when both are selected */}
            {selectedBanqueCode && selectedServiceCode && (
              <div className="pm-field">
                <label className="pm-field-label">3. Prestation</label>
                <div className="pm-search">
                  <MagnifyingGlassIcon className="pm-search-icon" />
                  <input
                    type="text"
                    placeholder="Rechercher (code, libelle)..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pm-search-input"
                  />
                </div>
              </div>
            )}

            {/* Selection summary and reset button - shown after step 1 */}
            {selectedBanqueCode && (
              <div className="pm-selection-summary">
                <div className="pm-selection-summary-text">
                  <span className="pm-selection-summary-item">
                    <BuildingLibraryIcon className="pm-selection-summary-icon" />
                    {banques?.find(b => b.banque_code === selectedBanqueCode)?.banque_name || selectedBanqueCode}
                  </span>
                  {selectedServiceCode && (
                    <span className="pm-selection-summary-item">
                      <WrenchScrewdriverIcon className="pm-selection-summary-icon" />
                      {services?.find(s => s.service_code === selectedServiceCode)?.service_name || selectedServiceCode}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  className="pm-selection-reset"
                  onClick={() => {
                    setSelectedBanqueCode(null);
                    setSelectedServiceCode(null);
                    setSearchTerm('');
                  }}
                  title="Recommencer la selection"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>

          {/* Prestation results */}
          {selectedBanqueCode && selectedServiceCode && (
            <div className="pm-results">
              {isLoadingPrestations ? (
                <div className="pm-loading">
                  <div className="pm-spinner" />
                </div>
              ) : filteredPrestations.length === 0 ? (
                <div className="pm-results-empty">
                  {searchTerm ? 'Aucune prestation trouvee' : 'Aucune prestation disponible'}
                </div>
              ) : (
                filteredPrestations.map((prestation) => (
                  <div
                    key={prestation.id}
                    className={`pm-results-item ${clickedPrestationId === prestation.id ? 'pm-results-item--clicked' : ''}`}
                    onClick={() => handleAddPrestation(prestation)}
                  >
                    <div className="pm-results-item-info">
                      <div className="pm-results-item-name">{prestation.prestation_name}</div>
                      <div className="pm-results-item-code">{prestation.prestation_code}</div>
                    </div>
                    <div className="pm-results-item-pricing">
                      <div className="pm-results-item-price">{formatCurrency(prestation.price_tvac)}</div>
                      <div className="pm-results-item-price-sub">{formatCurrency(prestation.price_htva)} HTVA</div>
                    </div>
                    <div className="pm-results-item-action">
                      <button
                        type="button"
                        className="pm-btn-add"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddPrestation(prestation);
                        }}
                      >
                        Ajouter
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Right: Cart */}
        <div className="pm-cart">
          <div className="pm-cart-header">
            <h3 className="pm-cart-title">Produits ({productCount})</h3>
            <div className="pm-cart-header-badges">
              <span className="pm-header-badge pm-header-badge--accent">
                Total: <span className="pm-header-badge-value">{formatCurrency(totalTvac)}</span>
              </span>
            </div>
          </div>

          {/* Delete error banner */}
          {deleteError && (
            <div className="pm-delete-error">
              <XMarkIcon className="pm-delete-error-icon" />
              <span>{deleteError}</span>
              <button
                type="button"
                className="pm-delete-error-dismiss"
                onClick={() => setDeleteError(null)}
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          )}

          <div className="pm-cart-list">
            {isLoadingProducts ? (
              <div className="pm-loading">
                <div className="pm-spinner" />
              </div>
            ) : !affaireProducts || affaireProducts.length === 0 ? (
              <div className="pm-cart-empty">
                <CubeIcon className="pm-cart-empty-icon" />
                <p className="pm-cart-empty-title">Aucun produit ajoute</p>
                <p className="pm-cart-empty-hint">Utilisez le catalogue a gauche pour ajouter des prestations</p>
              </div>
            ) : (
              <>
                {/* CUSTOMER track — billed directly to the customer on a SINGLE invoice */}
                {productGroups.CUSTOMER.length > 0 && (
                  <>
                    {hasMixedPayers && (
                      <div className="fact-products-section__header fact-products-section__header--customer">
                        <span className="fact-payer-chip fact-payer-chip--customer">Client</span>
                        <span className="fact-products-section__hint">
                          Facturation directe au client
                        </span>
                      </div>
                    )}
                    {productGroups.CUSTOMER.map((product) => (
                      <CartRow
                        key={product.id}
                        product={product}
                        isEditing={editingProductId === product.id}
                        isAnimating={animatingProductId === product.id}
                        isNew={newProductCode === product.product_code}
                        onEdit={() => setEditingProductId(product.id)}
                        onCancelEdit={() => setEditingProductId(null)}
                        onSaveEdit={(data) => handleSaveEdit(product.id, data)}
                        onDelete={() => handleDeleteProduct(product.id)}
                        onQtyChange={(qty) => handleQtyChange(product.id, qty)}
                        isUpdating={updateProductMutation.isPending}
                        isDeleting={deletingProductId === product.id}
                      />
                    ))}
                  </>
                )}

                {/* BANK_GROUPED track — billed to the bank on the next MONTHLY invoice */}
                {productGroups.BANK_GROUPED.length > 0 && (
                  <>
                    <div className="fact-products-section__header fact-products-section__header--bank">
                      <div className="fact-products-section__header-main">
                        <span className="fact-payer-chip fact-payer-chip--bank">
                          {productGroups.BANK_GROUPED[0].tarif_banque_name
                            || productGroups.BANK_GROUPED[0].tarif_banque_code
                            || productGroups.BANK_GROUPED[0].banque_code
                            || 'Banque'}
                        </span>
                        <span className="fact-products-section__hint">
                          Facturation groupée mensuelle
                          {primaryMonthlyTrack?.billing_contact_name
                            ? ` · ${primaryMonthlyTrack.billing_contact_name}`
                            : ''}
                        </span>
                      </div>
                      {primaryMonthlyTrack && bankSectionHasUnbilled && (
                        <button
                          type="button"
                          className="fact-monthly-cta"
                          onClick={handleOpenGroupedFacturation}
                        >
                          <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                          {bankSectionHasDraft
                            ? 'Voir le brouillon mensuel'
                            : 'Voir la prochaine facturation mensuelle'}
                        </button>
                      )}
                      {primaryMonthlyTrack && !primaryMonthlyTrack.status_eligible && bankSectionHasUnbilled && (
                        <div className="fact-products-section__note">
                          Statut affaire en attente avant inclusion dans une facture mensuelle.
                        </div>
                      )}
                    </div>
                    {productGroups.BANK_GROUPED.map((product) => (
                      <CartRow
                        key={product.id}
                        product={product}
                        isEditing={editingProductId === product.id}
                        isAnimating={animatingProductId === product.id}
                        isNew={newProductCode === product.product_code}
                        onEdit={() => setEditingProductId(product.id)}
                        onCancelEdit={() => setEditingProductId(null)}
                        onSaveEdit={(data) => handleSaveEdit(product.id, data)}
                        onDelete={() => handleDeleteProduct(product.id)}
                        onQtyChange={(qty) => handleQtyChange(product.id, qty)}
                        isUpdating={updateProductMutation.isPending}
                        isDeleting={deletingProductId === product.id}
                      />
                    ))}
                  </>
                )}
              </>
            )}
          </div>

          {summary && summary.totalHtva > 0 && (
            <div className="pm-totals-sticky">
              <div className="pm-totals-row">
                <span>Total HTVA</span>
                <span className="pm-totals-row-value">{formatCurrency(summary.totalHtva)}</span>
              </div>
              <div className="pm-totals-row">
                <span>TVA</span>
                <span className="pm-totals-row-value">{formatCurrency(summary.totalTva)}</span>
              </div>
              <div className="pm-totals-divider" />
              <div className="pm-totals-row pm-totals-row--total">
                <span>Total TVAC</span>
                <span className="pm-totals-row-value">{formatCurrency(summary.totalTvac)}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductsEmbeddedView;
